import { User, LoginResponse } from '../types';
import { API_CONFIG } from '../config/api';

class AuthService {
    private accessToken: string | null = null;
    private refreshToken: string | null = null;

    async adminLogin(email: string, password: string): Promise<LoginResponse> {
        let response: Response;
        try {
            response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ADMIN_LOGIN}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email, password })
            });
        } catch (error: any) {
            console.error('Admin login network error:', error);
            throw new Error('Unable to connect to server. Please check your internet connection and try again.');
        }

        let data: LoginResponse;
        try {
            data = await response.json();
        } catch {
            console.error('Admin login: non-JSON response, status:', response.status);
            throw new Error('Server returned an unexpected response. Please try again later.');
        }

        if (!response.ok) {
            // Extract error message - backend returns { error: { message: '...' } }
            const errorMessage = data.error?.message || data.message;

            if (response.status === 503) {
                throw new Error(errorMessage || 'Service temporarily unavailable. Please try again later.');
            } else if (response.status === 403) {
                throw new Error('Access denied. Admin privileges required.');
            } else if (response.status === 401) {
                throw new Error(errorMessage || 'Invalid email or password');
            } else if (response.status === 429) {
                throw new Error('Too many login attempts. Please try again later.');
            } else {
                throw new Error(errorMessage || 'Login failed. Please try again.');
            }
        }

        if (data.success && data.data) {
            // Normalize _id to id if backend returns MongoDB-style _id
            if (data.data.user && !data.data.user.id && (data.data.user as any)._id) {
                data.data.user.id = (data.data.user as any)._id;
            }

            this.accessToken = data.data.accessToken;
            this.refreshToken = data.data.refreshToken;

            localStorage.setItem('accessToken', data.data.accessToken);
            localStorage.setItem('refreshToken', data.data.refreshToken);
            localStorage.setItem('user', JSON.stringify(data.data.user));
        }

        return data;
    }

    async login(email: string, password: string): Promise<LoginResponse> {
        // Delegates to adminLogin — they are identical
        return this.adminLogin(email, password);
    }

    async loginWithToken(code: string): Promise<LoginResponse> {
        let response: Response;
        try {
            response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.LOGIN_WITH_TOKEN}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ code })
            });
        } catch (error: any) {
            console.error('Token login network error:', error);
            throw new Error('Unable to connect to server. Please check your internet connection and try again.');
        }

        let data: LoginResponse;
        try {
            data = await response.json();
        } catch {
            console.error('Token login: non-JSON response, status:', response.status);
            throw new Error('Server returned an unexpected response. Please try again later.');
        }

        if (!response.ok) {
            // Extract error message - backend returns { error: { message: '...' } }
            const errorMessage = data.error?.message || data.message;

            if (response.status === 503) {
                throw new Error(errorMessage || 'Service temporarily unavailable. Please try again later.');
            } else if (response.status === 401) {
                throw new Error('This invite link is invalid or has expired. Please ask your admin to generate a new one from app.getwemove.com');
            } else if (response.status === 403) {
                throw new Error('Account is not active. Please contact support.');
            } else {
                throw new Error(errorMessage || 'Authentication failed. Please try again.');
            }
        }

        if (data.success && data.data) {
            // Normalize _id to id if backend returns MongoDB-style _id
            if (data.data.user && !data.data.user.id && (data.data.user as any)._id) {
                data.data.user.id = (data.data.user as any)._id;
            }

            this.accessToken = data.data.accessToken;
            this.refreshToken = data.data.refreshToken;

            localStorage.setItem('accessToken', data.data.accessToken);
            localStorage.setItem('refreshToken', data.data.refreshToken);
            localStorage.setItem('user', JSON.stringify(data.data.user));
        }

        return data;
    }

    async logout(): Promise<void> {
        try {
            if (this.accessToken) {
                await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.LOGOUT}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${this.accessToken}`
                    }
                });
            }
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            this.accessToken = null;
            this.refreshToken = null;
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
        }
    }

    getAccessToken(): string | null {
        if (!this.accessToken) {
            this.accessToken = localStorage.getItem('accessToken');
        }
        return this.accessToken;
    }

    getStoredUser(): User | null {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            try {
                const parsed = JSON.parse(userStr);
                // Normalize _id to id for older stored data
                if (parsed && !parsed.id && parsed._id) {
                    parsed.id = parsed._id;
                }
                return parsed;
            } catch {
                return null;
            }
        }
        return null;
    }

    isAuthenticated(): boolean {
        return !!this.getAccessToken();
    }

    /**
     * Set tokens directly from registration response (skip separate login call)
     */
    setTokensFromRegistration(tokens: { accessToken: string; refreshToken: string }, user: User): void {
        this.accessToken = tokens.accessToken;
        this.refreshToken = tokens.refreshToken;

        localStorage.setItem('accessToken', tokens.accessToken);
        localStorage.setItem('refreshToken', tokens.refreshToken);
        localStorage.setItem('user', JSON.stringify(user));
    }

    /**
     * Request password reset - sends verification code to email
     */
    async forgotPassword(email: string): Promise<{ success: boolean; message: string }> {
        let response: Response;
        try {
            response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.FORGOT_PASSWORD}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email })
            });
        } catch {
            throw new Error('Unable to connect to server. Please check your internet connection and try again.');
        }

        let data: any;
        try {
            data = await response.json();
        } catch {
            throw new Error('Server returned an unexpected response. Please try again later.');
        }

        if (!response.ok) {
            throw new Error(data.message || 'Failed to send reset code. Please try again.');
        }

        return data;
    }

    /**
     * Verify the reset code sent to email
     */
    async verifyResetCode(email: string, code: string): Promise<{ success: boolean; data: { resetToken: string } }> {
        let response: Response;
        try {
            response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.VERIFY_RESET_CODE}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email, code })
            });
        } catch {
            throw new Error('Unable to connect to server. Please check your internet connection and try again.');
        }

        let data: any;
        try {
            data = await response.json();
        } catch {
            throw new Error('Server returned an unexpected response. Please try again later.');
        }

        if (!response.ok) {
            if (response.status === 400) {
                throw new Error('Invalid or expired code. Please try again.');
            }
            throw new Error(data.message || 'Verification failed. Please try again.');
        }

        return data;
    }

    /**
     * Reset password with the token from verification
     */
    async resetPassword(token: string, newPassword: string): Promise<{ success: boolean; message: string }> {
        let response: Response;
        try {
            response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.RESET_PASSWORD}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ token, newPassword })
            });
        } catch {
            throw new Error('Unable to connect to server. Please check your internet connection and try again.');
        }

        let data: any;
        try {
            data = await response.json();
        } catch {
            throw new Error('Server returned an unexpected response. Please try again later.');
        }

        if (!response.ok) {
            if (response.status === 400) {
                throw new Error('Invalid or expired reset token. Please start over.');
            }
            throw new Error(data.message || 'Password reset failed. Please try again.');
        }

        return data;
    }
}

export const authService = new AuthService();